import { useCallback, useEffect, useRef, useState } from "react";

import {
	SendMessagesRequest,
	useCancelPendingSendMutation,
	useSendMessagesDeferredMutation,
} from "@/pages/Private/redux/schedule/schedule.api";

/** How long the Undo toast stays actionable in the UI. */
export const UNDO_WINDOW_MS = 5000;

/**
 * Server-side hold: slightly longer than the UI window so a cancel fired at
 * the very end of the toast still reaches the API before the job executes.
 */
export const UNDO_WINDOW_SERVER_SECONDS = 8;

export interface PendingUndoSend {
	pendingSendId: string;
	listPersonId: number;
	messageCount: number;
	/** The send parks behind a pending invitation: days after the acceptance, else null (PREC-4216). */
	acceptanceDelayDays: number | null;
}

interface UseUndoableSendOptions {
	/**
	 * Runs when the undo window elapses (or an undo arrives too late): the send
	 * is definitive now, so the host removes the lead from the inbox
	 * (auto-advance) and fires its own post-send effects.
	 */
	onCommit: (listPersonId: number) => void;
	/** Runs after a successful undo — the UI was never touched, so this is just feedback. */
	onUndone?: () => void;
	/** Runs when undoing failed because the window had already closed server-side. */
	onUndoFailed?: () => void;
}

/**
 * Undo-send state machine around the deferred send-messages endpoint.
 *
 * `send` enqueues the messages server-side (held as a delayed job) and starts
 * the local undo window. During the window nothing else changes — drafts and
 * the inbox stay untouched, matching the mockup where the thread stays put
 * until the toast expires. `undo` cancels the held job; when the window
 * elapses (or the hook unmounts / the host commits early on a lead switch),
 * `onCommit` finalizes the UI.
 */
export const useUndoableSend = ({ onCommit, onUndone, onUndoFailed }: UseUndoableSendOptions) => {
	const [pendingSend, setPendingSend] = useState<PendingUndoSend | null>(null);
	const [sendDeferred, { isLoading: isSending }] = useSendMessagesDeferredMutation();
	const [cancelPendingSend, { isLoading: isUndoing }] = useCancelPendingSendMutation();

	// Refs mirror the pending state so commit/undo stay stable across renders
	// and the unmount cleanup sees the latest value.
	const pendingRef = useRef<PendingUndoSend | null>(null);
	const timerRef = useRef<number | null>(null);
	const onCommitRef = useRef(onCommit);

	onCommitRef.current = onCommit;

	const clearTimer = useCallback(() => {
		if (timerRef.current !== null) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const takePending = useCallback((): PendingUndoSend | null => {
		const pending = pendingRef.current;

		pendingRef.current = null;
		setPendingSend(null);

		return pending;
	}, []);

	/** Finalizes the pending send in the UI. Safe to call when nothing is pending. */
	const commit = useCallback(() => {
		clearTimer();
		const pending = takePending();

		if (pending) {
			onCommitRef.current(pending.listPersonId);
		}
	}, [clearTimer, takePending]);

	const send = useCallback(
		async (request: SendMessagesRequest, acceptanceDelayDays: number | null = null) => {
			const { pendingSendId } = await sendDeferred({
				...request,
				undoWindowSeconds: UNDO_WINDOW_SERVER_SECONDS,
			}).unwrap();

			const pending: PendingUndoSend = {
				pendingSendId,
				listPersonId: request.listPersonId,
				messageCount: request.messages.length,
				acceptanceDelayDays,
			};

			pendingRef.current = pending;
			setPendingSend(pending);
			clearTimer();
			timerRef.current = window.setTimeout(commit, UNDO_WINDOW_MS);
		},
		[sendDeferred, clearTimer, commit]
	);

	const undo = useCallback(async () => {
		clearTimer();
		const pending = takePending();

		if (!pending) {
			return;
		}

		try {
			await cancelPendingSend(pending.pendingSendId).unwrap();
			onUndone?.();
		} catch {
			// The window closed server-side (404/400) — the send went out after all.
			onCommitRef.current(pending.listPersonId);
			onUndoFailed?.();
		}
	}, [clearTimer, takePending, cancelPendingSend, onUndone, onUndoFailed]);

	// A pending send left hanging on unmount is already enqueued server-side —
	// finalize the UI bookkeeping instead of dropping it silently.
	useEffect(() => {
		return () => {
			if (timerRef.current !== null) {
				window.clearTimeout(timerRef.current);
			}

			const pending = pendingRef.current;

			pendingRef.current = null;

			if (pending) {
				onCommitRef.current(pending.listPersonId);
			}
		};
	}, []);

	return { send, undo, commit, pendingSend, isSending, isUndoing };
};
